import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const ActionableDetail = () => {
  const { uat_id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);

  const hiddenFields = ["id", "company_id", "uat_id", "evidence_file"];

  useEffect(() => {
    axios.get(`/api/actionables/detail/${uat_id}/`).then((res) => {
      setData(res.data);
    });
  }, [uat_id]);

  const handleSubmit = async () => {
    if (!file) {
      alert("Please select a file");
      return;
    }

    const formData = new FormData();
    formData.append("evidence", file);

    await axios.post(`/api/actionables/upload/${uat_id}/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    alert("Evidence uploaded successfully");
    navigate("/actionables-glance");
  };

  const getStatusStyle = (status) => {
    if (!status) return {};
    const colorMap = {
      New: "#3b82f6",
      Completed: "#10b981",
      Pending: "#ef4444",
      "Completed After Deadline": "#f59e0b",
    };
    const color = colorMap[status] || "#6b7280";
    return {
      backgroundColor: `${color}15`,
      borderLeft: `4px solid ${color}`,
      color,
      fontWeight: "600",
      paddingLeft: "12px",
    };
  };

  if (!data) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading actionable details...</div>
      </div>
    );
  }

  // ✅ SHOW UPLOAD ONLY FOR NEW + PENDING
  const showUploadSection =
    data.status === "New" || data.status === "Pending";

  const username = data.username || "—";
  const companyName = data.company_name || "—";
  const actionableNumber = data.actionable_number || "—";

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button
          style={styles.backBtn}
          onClick={() => navigate("/actionables-glance")}
        >
          ← Back
        </button>
        <h1 style={styles.title}>Actionable Details</h1>
      </div>

      {/* Summary Section */}
      <div style={styles.summaryContainer}>
        <div style={styles.summaryItem}>
          <span style={styles.emoji}>👤</span>
          <div>
            <div style={styles.label}>Username</div>
            <div style={styles.value}>{username}</div>
          </div>
        </div>

        <div style={styles.summaryItem}>
          <span style={styles.emoji}>🏢</span>
          <div>
            <div style={styles.label}>Company</div>
            <div style={styles.value}>{companyName}</div>
          </div>
        </div>

        <div style={styles.summaryItem}>
          <span style={styles.emoji}>#️⃣</span>
          <div>
            <div style={styles.label}>Actionable Number</div>
            <div style={styles.value}>{actionableNumber}</div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div style={styles.detailsContainer}>
        <h2 style={styles.sectionHeading}>Detailed Information</h2>
        <div style={styles.detailsList}>
          {Object.entries(data)
            .filter(
              ([key]) =>
                !hiddenFields.includes(key) &&
                !["username", "company_name", "actionable_number"].includes(key)
            )
            .map(([key, value], i) => {
              const isStatus = key.toLowerCase() === "status";
              return (
                <div
                  key={key}
                  style={{
                    ...styles.detailRow,
                    ...(i % 2 === 0 ? styles.rowEven : styles.rowOdd),
                    ...(isStatus ? getStatusStyle(value) : {}),
                  }}
                >
                  <div style={styles.detailKey}>
                    {key.replace(/_/g, " ").toUpperCase()}
                  </div>
                  <div style={styles.detailValue}>{value || "—"}</div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Upload Section — only for NEW + PENDING */}
      {showUploadSection && (
        <div style={styles.uploadContainer}>
          <h2 style={styles.uploadHeading}>Upload Evidence</h2>
          <p style={styles.uploadText}>
            Attach supporting evidence to complete this actionable.
          </p>
          <div style={styles.uploadBox}>
            <input
              type="file"
              onChange={(e) => setFile(e.target.files[0])}
              style={styles.fileInput}
            />
            <button style={styles.uploadBtn} onClick={handleSubmit}>
              ⬆ Upload File
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

/* Refined Table-like Layout Styles */
const styles = {
  page: {
    padding: "40px 50px",
    background: "linear-gradient(135deg, #f6f9fc, #e9f0fa)",
    minHeight: "100vh",
    fontFamily: "Segoe UI, Roboto, sans-serif",
    color: "#333",
  },
  header: {
    borderBottom: "4px solid #007bff",
    paddingBottom: "15px",
    marginBottom: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#007bff",
    fontSize: "15px",
    cursor: "pointer",
    fontWeight: "500",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e1e1e",
  },
  summaryContainer: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(10px)",
    borderRadius: "14px",
    padding: "20px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
    marginBottom: "35px",
    flexWrap: "wrap",
    gap: "15px",
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: "240px",
  },
  emoji: {
    fontSize: "22px",
  },
  label: {
    fontSize: "13px",
    color: "#777",
    textTransform: "uppercase",
  },
  value: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#111",
  },
  detailsContainer: {
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    overflow: "hidden",
  },
  sectionHeading: {
    fontSize: "20px",
    fontWeight: "600",
    background: "#f1f5fb",
    padding: "15px 20px",
    borderBottom: "1px solid #e1e6ee",
  },
  detailsList: {
    display: "flex",
    flexDirection: "column",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "14px 20px",
    alignItems: "center",
  },
  rowEven: {
    background: "#fafbfc",
  },
  rowOdd: {
    background: "#f3f6fa",
  },
  detailKey: {
    fontWeight: "600",
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#555",
    flex: "1",
  },
  detailValue: {
    flex: "2",
    textAlign: "right",
    fontSize: "15px",
    fontWeight: "500",
    color: "#222",
    wordWrap: "break-word",
  },
  uploadContainer: {
    marginTop: "40px",
    background: "#f8fbff",
    border: "2px dashed #a7c8ff",
    borderRadius: "14px",
    padding: "30px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  uploadHeading: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#004aad",
  },
  uploadText: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "15px",
  },
  uploadBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  fileInput: {
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    background: "#fff",
  },
  uploadBtn: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    fontSize: "15px",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "background-color 0.3s, transform 0.2s",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
  },
  loadingText: {
    color: "#666",
    fontSize: "18px",
    fontWeight: "500",
  },
};

export default ActionableDetail;
